import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
type StorybookPage = {
  pageNumber: number;
  image: string;
  text: string;
};
const samplePages: StorybookPage[] = [{
  pageNumber: 1,
  image: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=1600&h=1200&fit=crop',
  text: 'Long ago, when gods walked among mortals, a powerful hero named Achilles was born. His mother was Thetis, a beautiful sea goddess, and his father was King Peleus.'
}, {
  pageNumber: 2,
  image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1600&h=1200&fit=crop',
  text: 'As a young boy, Achilles showed extraordinary strength and courage. His mother knew he was destined for greatness, but also feared for his life.'
}, {
  pageNumber: 3,
  image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&h=1200&fit=crop',
  text: 'To protect him, Thetis dipped baby Achilles in the magical River Styx, making him invulnerable everywhere except his heel, where she held him.'
}, {
  pageNumber: 4,
  image: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=1600&h=1200&fit=crop',
  text: 'Years later, Achilles became the greatest warrior of his time, leading armies and winning countless battles with his unmatched skill and bravery.'
}, {
  pageNumber: 5,
  image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1600&h=1200&fit=crop',
  text: 'His legend grew with every victory, and songs were sung about his heroic deeds throughout the ancient world.'
}, {
  pageNumber: 6,
  image: 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=1600&h=1200&fit=crop',
  text: 'But fate had other plans, and the prophecy his mother feared would eventually come true in the great war at Troy.'
}, {
  pageNumber: 7,
  image: 'https://images.unsplash.com/photo-1473186578172-c141e6798cf4?w=1600&h=1200&fit=crop',
  text: 'Even today, thousands of years later, the story of Achilles reminds us of the power of courage, loyalty, and the choices we make.'
}, {
  pageNumber: 8,
  image: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1600&h=1200&fit=crop',
  text: 'And so the tale of the greatest Greek hero lives on, inspiring generations to be brave, strong, and true to themselves.'
}];
type StorybookViewerProps = {
  storybookId: number;
  onClose: () => void;
};
export function StorybookViewer({
  storybookId,
  onClose
}: StorybookViewerProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = samplePages.length;
  const storybook = {
    title: 'Achilles',
    totalPages: totalPages
  };
  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };
  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNextPage();
      } else if (e.key === 'ArrowLeft') {
        goToPreviousPage();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, onClose]);
  const page = samplePages[currentPage];
  return <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-id="element-2466">
      {/* Header */}
      <div className="absolute top-6 left-6 text-white" data-id="element-2467">
        <h2 className="text-2xl font-bold" data-id="element-2468">{storybook.title}</h2>
        <p className="text-gray-400" data-id="element-2469">
          Page {currentPage + 1} of {totalPages}
        </p>
      </div>

      {/* Close Button */}
      <button onClick={onClose} className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors" aria-label="Close viewer" data-id="element-2470">
        <X className="w-6 h-6 text-white" data-id="element-2471" />
      </button>

      {/* Previous Button */}
      {currentPage > 0 && <button onClick={goToPreviousPage} className="absolute left-6 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors" aria-label="Previous page" data-id="element-2472">
          <ChevronLeft className="w-8 h-8 text-white" data-id="element-2473" />
        </button>}

      {/* Next Button */}
      {currentPage < totalPages - 1 && <button onClick={goToNextPage} className="absolute right-6 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors" aria-label="Next page" data-id="element-2474">
          <ChevronRight className="w-8 h-8 text-white" data-id="element-2475" />
        </button>}

      {/* Page Content */}
      <div className="max-w-7xl w-full px-20" data-id="element-2476">
        <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-2477">
          {/* Image */}
          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl" data-id="element-2478">
            <img src={page.image} alt={`Page ${page.pageNumber}`} className="w-full h-full object-cover" data-id="element-2479" />
          </div>

          {/* Text */}
          <div className="bg-white rounded-2xl p-12 shadow-2xl" data-id="element-2480">
            <div className="text-sm text-gray-500 mb-6" data-id="element-2481">
              PAGE {page.pageNumber}
            </div>
            <p className="text-2xl text-gray-900 leading-relaxed font-serif" data-id="element-2482">
              {page.text}
            </p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-400 text-sm" data-id="element-2483">
        Use arrow keys to navigate • Press ESC to exit
      </div>
    </div>;
}